import { useLocale } from "stores/hooks/locale";
import { parse } from "lib/locale";
import { ListEntityField } from "models/list-entity-field";
import { Fragment, FunctionComponent } from "react";
import Grid from "./grid";
import Row from "./row";
import Col from "./col";
import ValuePair from "components/valuepair";
import { parseUserValues } from "lib/collection-values";
import { EntityCollection } from "models/list-user-entity-collection";

interface ValueGridProps {
  listEntityFields: ListEntityField[];
  entityCollections: EntityCollection[];
}

const ValueGrid: FunctionComponent<ValueGridProps> = ({
  listEntityFields,
  entityCollections,
}) => {
  const locale = useLocale();

  const fieldRows: ListEntityField[][] = [];
  let row: ListEntityField[] = [];
  listEntityFields.forEach((field, index) => {
    // if the row has space, add field
    if (row.length <= 2) {
      row.push(field);
    }

    // if the row is full, add to collection (and reset row)
    if (row.length === 2 || index === listEntityFields.length - 1) {
      fieldRows.push(row);
      row = [];
    }
  });

  const buildField = (field: ListEntityField) => {
    const { name } = field;
    const label = parse(name, locale);
    const values = parseUserValues(field, entityCollections, locale);
    const joined = values.join(", ");
    return <ValuePair label={label}>{joined || "-"}</ValuePair>;
  };

  // check row lengths
  const gridRows: JSX.Element[][] = [];
  fieldRows.forEach((row) => {
    const [field1, field2] = row;

    // special case, the row only has 1 item
    if (field2 === undefined) {
      const row = [buildField(field1)];
      if (field1.large === false) {
        row.push(<Fragment />);
      }

      return gridRows.push(row);
    }

    // if both are small
    if (!field1.large && !field2.large) {
      return gridRows.push([buildField(field1), buildField(field2)]);
      // either is large or both are large ==> split row
    } else {
      const row1 = [buildField(field1)];
      if (field1.large === false) {
        row1.push(<Fragment />);
      }
      const row2 = [buildField(field2)];
      if (field2.large === false) {
        row2.push(<Fragment />);
      }
      return gridRows.push(row1, row2);
    }
  });

  return (
    <Grid>
      {gridRows.map((gridRow, rowIndex) => {
        return (
          <Fragment key={rowIndex}>
            <Row>
              {gridRow.map((field, columnIndex) => {
                return <Col key={columnIndex}>{field}</Col>;
              })}
            </Row>
          </Fragment>
        );
      })}
    </Grid>
  );
};

export default ValueGrid;
